// Blog Feed - Display latest blog posts from all categories
import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import './BlogFeed.css';

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'azure', label: 'Azure', color: '#0078D4' },
  { id: 'ai', label: 'AI', color: '#FF6B6B' },
  { id: 'm365', label: 'M365', color: '#7FBA00' },
];

function BlogFeed() {
  const history = useHistory();
  const [blogs, setBlogs] = useState([]);
  const [allBlogs, setAllBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    fetchAllBlogs();
  }, []);

  const fetchAllBlogs = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/blogs');
      if (response.ok) {
        const data = await response.json();
        const sorted = data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setAllBlogs(sorted);
        setBlogs(sorted.slice(0, 6));
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter blogs by search query and category
  const filteredBlogs = useMemo(() => {
    let results = allBlogs;

    if (activeCategory !== 'all') {
      results = results.filter(b => b.category === activeCategory);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      results = results.filter(b =>
        b.title.toLowerCase().includes(q) ||
        b.category.toLowerCase().includes(q) ||
        (b.tags && b.tags.some(t => t.toLowerCase().includes(q)))
      );
    }

    return search.trim() || activeCategory !== 'all' ? results : results.slice(0, 6);
  }, [allBlogs, search, activeCategory]);

  // All unique tags across all blogs for suggestions
  const allTags = useMemo(() => {
    const tagSet = new Set();
    allBlogs.forEach(b => (b.tags || []).forEach(t => tagSet.add(t)));
    return [...tagSet].sort();
  }, [allBlogs]);

  const getCategoryColor = (category) => {
    return category === 'azure' ? '#0078D4' : category === 'ai' ? '#FF6B6B' : '#7FBA00';
  };

  const handleBlogClick = (blogId) => {
    history.push(`/blog/${blogId}`);
  };

  const handleTagClick = (tag) => {
    setSearch(tag);
    setActiveCategory('all');
  };

  const isSearchActive = search.trim() || activeCategory !== 'all';

  if (loading) {
    return (
      <div className="blog-feed-container">
        <div className="blog-feed-loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading latest posts...</p>
        </div>
      </div>
    );
  }

  if (allBlogs.length === 0) {
    return null;
  }

  return (
    <div className="blog-feed-container">
      <div className="blog-feed-header">
        <h2>Latest from Our Blog</h2>
        <p>Explore insights on Azure, AI, M365, and cloud technologies</p>

        {/* Search bar */}
        <div className="blog-search-wrapper">
          <div className="blog-search-input-row">
            <i className="fas fa-search blog-search-icon"></i>
            <input
              type="text"
              className="blog-search-input"
              placeholder="Search by tag or category..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="blog-search-clear" onClick={() => setSearch('')}>
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>

          {/* Category filter pills */}
          <div className="blog-category-pills">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                className={`blog-cat-pill${activeCategory === cat.id ? ' active' : ''}`}
                style={activeCategory === cat.id && cat.color ? { background: cat.color, borderColor: cat.color, color: '#fff' } : {}}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Tag suggestions (shown when nothing typed yet) */}
          {!search && allTags.length > 0 && (
            <div className="blog-tag-suggestions">
              {allTags.slice(0, 12).map(tag => (
                <button key={tag} className="blog-tag-suggestion" onClick={() => handleTagClick(tag)}>
                  #{tag}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Results count when filtering */}
      {isSearchActive && (
        <p className="blog-results-count">
          {filteredBlogs.length === 0
            ? 'No posts found'
            : `${filteredBlogs.length} post${filteredBlogs.length !== 1 ? 's' : ''} found`}
          {' '}
          <button className="blog-clear-filters" onClick={() => { setSearch(''); setActiveCategory('all'); }}>
            Clear filters
          </button>
        </p>
      )}

      <div className="blog-feed-grid">
        {filteredBlogs.map((blog) => (
          <div
            key={blog.id}
            className="blog-feed-card"
            onClick={() => handleBlogClick(blog.id)}
          >
            {blog.thumbnail && (
              <div className="blog-feed-card-thumbnail">
                <img src={blog.thumbnail} alt={blog.title} style={{ objectPosition: `center ${blog.thumbnailPosition ?? 50}%` }} />
              </div>
            )}
            <div className={`blog-feed-card-body${blog.thumbnail ? '' : ' blog-feed-card-body--no-thumb'}`}>
              <div className="blog-feed-card-author-row">
                {blog.authorId
                  ? <img src={`/api/profile-avatar?userId=${blog.authorId}`} alt={blog.author} className="blog-feed-author-avatar" onError={e => { e.target.style.display='none'; }} />
                  : <i className="fas fa-user-circle blog-feed-author-icon"></i>
                }
                <span className="blog-feed-author-name">{blog.author}</span>
                <span className="blog-feed-author-sep">·</span>
                <span className="blog-feed-date">
                  {new Date(blog.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>

              <h3 className="blog-feed-title">{blog.title}</h3>

              <p className="blog-feed-excerpt">{blog.excerpt}</p>

              <div className="blog-feed-footer">
                <div className="blog-feed-tags">
                  {(blog.tags || []).slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="blog-feed-tag"
                      onClick={e => { e.stopPropagation(); handleTagClick(tag); }}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
                <div className="blog-feed-footer-right">
                  {blog.viewCount > 0 && (
                    <span className="blog-feed-views">
                      <i className="far fa-eye"></i> {blog.viewCount.toLocaleString()}
                    </span>
                  )}
                  <div className="blog-feed-read-more">
                    Read More <i className="fas fa-arrow-right"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredBlogs.length === 0 && isSearchActive && (
        <div className="blog-empty-state">
          <i className="fas fa-search"></i>
          <p>No posts match your search. Try a different tag or category.</p>
        </div>
      )}

      <div className="blog-feed-view-all">
        <button
          className="btn-view-all"
          onClick={() => history.push('/content')}
        >
          View All Articles <i className="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>
  );
}

export default BlogFeed;
